/**
 * "Magic wand" shape assist: turns a rough freehand stroke into the perfect
 * geometric shape it was probably meant to be, the same idea as the shape
 * assist in Miro or Apple Notes.
 *
 * This runs once, on pointerup, over the raw (unsimplified) sample points. It
 * never mutates the stroke — the caller decides whether to keep the ink or
 * swap it for the recognised shape, so a false positive costs nothing more
 * than pressing undo.
 */
import { boundsOfPoints, closestPointOnSegment, simplify, vDist } from './geometry.js';
import type { Rect, Vec } from './types.js';

export type RecognizedKind = 'rect' | 'ellipse' | 'triangle' | 'rightTriangle' | 'diamond' | 'line';

export interface RecognizedShape {
  kind: RecognizedKind;
  /** Axis-aligned bounding box the shape should be drawn into. */
  box: Rect;
  /** Only set for `line`: the two endpoints, straightened. */
  points?: [Vec, Vec];
}

const MIN_DIAGONAL = 22;
/** Radians — how close a corner must be to 90° to count as a right angle. */
const RIGHT_ANGLE_SLOP = (18 * Math.PI) / 180;

function pathLength(points: readonly Vec[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += vDist(points[i - 1]!, points[i]!);
  return total;
}

/** Shoelace formula; sign indicates winding, magnitude is the enclosed area. */
function signedArea(points: readonly Vec[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

function maxDeviationFromSegment(points: readonly Vec[], a: Vec, b: Vec): number {
  let max = 0;
  for (const p of points) {
    const c = closestPointOnSegment(a, b, p);
    const d = vDist(p, c);
    if (d > max) max = d;
  }
  return max;
}

/** Interior angle at `vertex` formed by `prev → vertex → next`, in radians. */
function angleAt(prev: Vec, vertex: Vec, next: Vec): number {
  const ux = prev.x - vertex.x;
  const uy = prev.y - vertex.y;
  const vx = next.x - vertex.x;
  const vy = next.y - vertex.y;
  const lu = Math.hypot(ux, uy) || 1;
  const lv = Math.hypot(vx, vy) || 1;
  const cos = (ux * vx + uy * vy) / (lu * lv);
  return Math.acos(Math.max(-1, Math.min(1, cos)));
}

/**
 * Assign each vertex to the nearest unused target and sum the distances —
 * a cheap stand-in for the optimal bipartite matching of three points.
 */
function matchScore(verts: readonly Vec[], targets: readonly Vec[]): number {
  const used = new Set<number>();
  let score = 0;
  for (const v of verts) {
    let best = Infinity;
    let bestI = -1;
    for (let i = 0; i < targets.length; i++) {
      if (used.has(i)) continue;
      const d = vDist(v, targets[i]!);
      if (d < best) {
        best = d;
        bestI = i;
      }
    }
    used.add(bestI);
    score += best;
  }
  return score;
}

/**
 * Distinguishes a rectangle from a diamond among 4-ish simplified vertices by
 * checking whether the vertices sit near the box corners (rect) or near the
 * midpoints of its edges (diamond).
 */
function looksLikeDiamond(verts: readonly Vec[], box: Rect): boolean {
  if (verts.length < 4) return false;
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.w, y: box.y },
    { x: box.x + box.w, y: box.y + box.h },
    { x: box.x, y: box.y + box.h },
  ];
  const mids = [
    { x: cx, y: box.y },
    { x: box.x + box.w, y: cy },
    { x: cx, y: box.y + box.h },
    { x: box.x, y: cy },
  ];
  const diag = Math.hypot(box.w, box.h) || 1;

  let cornerScore = 0;
  let midScore = 0;
  for (const v of verts) {
    const nearestCorner = Math.min(...corners.map((c) => vDist(v, c)));
    const nearestMid = Math.min(...mids.map((m) => vDist(v, m)));
    cornerScore += nearestCorner;
    midScore += nearestMid;
  }
  // Diamond wins when its vertices sit reliably closer to edge midpoints than
  // to corners, by a margin big enough to not be noise.
  return midScore < cornerScore - diag * 0.08;
}

/**
 * A right triangle's corners sit on three AABB corners; an isosceles "house"
 * triangle has an apex on the top edge midpoint. Prefer rightTriangle when
 * the corner fit wins, or when a clear ~90° angle agrees with that fit.
 */
function looksLikeRightTriangle(verts: readonly Vec[], box: Rect): boolean {
  if (verts.length < 3) return false;
  const tri = verts.slice(0, 3);
  const diag = Math.hypot(box.w, box.h) || 1;

  let bestRightErr = Infinity;
  for (let i = 0; i < 3; i++) {
    const ang = angleAt(tri[(i + 2) % 3]!, tri[i]!, tri[(i + 1) % 3]!);
    bestRightErr = Math.min(bestRightErr, Math.abs(ang - Math.PI / 2));
  }
  const hasRightAngle = bestRightErr < RIGHT_ANGLE_SLOP;

  const corners: Vec[] = [
    { x: box.x, y: box.y },
    { x: box.x + box.w, y: box.y },
    { x: box.x + box.w, y: box.y + box.h },
    { x: box.x, y: box.y + box.h },
  ];
  let cornerScore = Infinity;
  for (let skip = 0; skip < 4; skip++) {
    const subset = corners.filter((_, i) => i !== skip);
    cornerScore = Math.min(cornerScore, matchScore(tri, subset));
  }

  const cx = box.x + box.w / 2;
  const isoscelesLandmarks: Vec[] = [
    { x: cx, y: box.y },
    { x: box.x, y: box.y + box.h },
    { x: box.x + box.w, y: box.y + box.h },
  ];
  const isoScore = matchScore(tri, isoscelesLandmarks);

  if (cornerScore < isoScore - diag * 0.06) return true;
  if (hasRightAngle && cornerScore <= isoScore + diag * 0.04) return true;
  return false;
}

/**
 * Attempts to recognise a closed or straight-line stroke.
 *
 * Returns null when the stroke does not look confidently like a supported
 * primitive — callers should fall back to keeping it as ink.
 */
export function recognizeStroke(rawPoints: readonly Vec[]): RecognizedShape | null {
  if (rawPoints.length < 4) return null;

  const box = boundsOfPoints(rawPoints);
  const diag = Math.hypot(box.w, box.h);
  if (diag < MIN_DIAGONAL) return null;

  const first = rawPoints[0]!;
  const last = rawPoints[rawPoints.length - 1]!;
  const closingGap = vDist(first, last);
  const closed = closingGap < Math.max(26, diag * 0.22);

  if (!closed) {
    // A confidently straight, open stroke gets snapped into a ruler-straight
    // line rather than forced into a closed shape.
    const maxDev = maxDeviationFromSegment(rawPoints, first, last);
    if (diag > 48 && maxDev < Math.max(7, diag * 0.045)) {
      return { kind: 'line', box, points: [first, last] };
    }
    return null;
  }

  const loop = [...rawPoints];
  const perimeter = pathLength(loop) + closingGap;
  const area = Math.abs(signedArea(loop));
  // 1 for a perfect circle, ~0.785 for a square, lower for spikier polygons.
  const circularity = perimeter > 0 ? (4 * Math.PI * area) / (perimeter * perimeter) : 0;

  // Aggressive simplification collapses a polygon down to its real corners,
  // while a smooth ellipse still needs many points to stay within tolerance.
  const simplified = simplify(loop, Math.max(diag * 0.045, 4));
  let verts = simplified;
  if (verts.length > 1 && vDist(verts[0]!, verts[verts.length - 1]!) < diag * 0.12) {
    verts = verts.slice(0, -1);
  }
  const n = verts.length;

  if (circularity > 0.82 && n >= 6) {
    return { kind: 'ellipse', box };
  }
  if (n <= 3) {
    if (n === 3 && looksLikeRightTriangle(verts, box)) {
      return { kind: 'rightTriangle', box };
    }
    return { kind: 'triangle', box };
  }
  if (n >= 4 && n <= 6) {
    return { kind: looksLikeDiamond(verts, box) ? 'diamond' : 'rect', box };
  }
  if (circularity > 0.7) {
    return { kind: 'ellipse', box };
  }
  return null;
}
